import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Snackbar } from 'src/components/snackbar';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { PATHS } from 'src/constants/paths';
import {
  useCreateTemplateVariations,
  useGetProduct,
  useModal,
} from 'src/hooks';
import { useGetDyTemplates } from 'src/hooks/api/product/dyTemplates';
import { IDyTemplate } from 'src/hooks/api/product/dyTemplates/helper';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { useNavigateBlocker } from 'src/hooks/navigation';
import { lang } from 'src/utils';

import { useUnloadTempData } from '../../editVariationTemplatePage/hooks/useUnloadTempData';
import { VariationMap } from '../modules/variationTemplateMappingStep/VariationTemplateMappingStep.utils';

export const useAddVariationTemplatePage = () => {
  const createTemplateVariations = useCreateTemplateVariations();
  const { productCode = '', variationCode = '' } = useParams();
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = useState<IDyTemplate | null>(
    null,
  );
  const [activeStep, setActiveStep] = useState(0);
  const [variationMap, setVariationMap] = useState<VariationMap>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const discardModal = useModal();
  const skipBlockRef = useRef(false);

  const { data: product, isLoading: isProductLoading } =
    useGetProduct(productCode);
  const { data: templates = [], isLoading } = useGetDyTemplates(true);
  const { shouldRemoveItemRef } = useUnloadTempData(productCode);

  const initialVariationMap = useMemo<VariationMap>(() => {
    if (!selectedTemplate) return {};

    const matchedVariation = product?.productVariations?.find(
      (v) => v.product_code === variationCode,
    );
    const templateVariations = matchedVariation?.variation_template?.variations;

    return Object.fromEntries(
      Array.from({
        length: selectedTemplate.number_of_variations || 0,
      }).map((_, index) => [
        index + 1,
        {
          productCode: index === 0 ? productCode : '',
          variationCode: index === 0 ? variationCode : '',
          showFlavors:
            templateVariations?.find((v) => v.order === index + 1)?.config
              ?.showFlavors || selectedTemplate.number_of_variations === 1,
        },
      ]),
    );
  }, [
    selectedTemplate,
    product?.productVariations,
    variationCode,
    productCode,
  ]);

  const isVariationMapDirty = useMemo(
    () => !isEqual(variationMap, initialVariationMap),
    [initialVariationMap, variationMap],
  );

  const shouldBlockNavigation = useCallback(
    () => !skipBlockRef.current && isVariationMapDirty && !isSubmitting,
    [isVariationMapDirty, isSubmitting],
  );

  useNavigateBlocker(shouldBlockNavigation);

  const skipNextBlock = () => {
    skipBlockRef.current = true;
  };

  useEffect(() => {
    if (!!productCode && !!variationCode && !isProductLoading && !product) {
      Snackbar.error(lang.variationCodeNotFound);
      productCode ? navigate(PATHS.PRODUCT_EDIT(productCode)) : navigate(-1);
    }
  }, [productCode, variationCode, navigate, isProductLoading, product]);

  const handleDiscard = () => {
    if (isVariationMapDirty) {
      discardModal.onOpenModal();

      return;
    }

    shouldRemoveItemRef.current = false;
    navigate(`${PATHS.PRODUCT_EDIT(productCode)}#${variationCode}`);
  };

  const handleDiscardConfirm = () => {
    skipNextBlock();
    shouldRemoveItemRef.current = false;
    discardModal.onCloseModal();
    navigate(`${PATHS.PRODUCT_EDIT(productCode)}#${variationCode}`);
  };

  const handleNext = () => {
    if (selectedTemplate) {
      setVariationMap((prev) =>
        Object.keys(prev).length > 0 ? prev : initialVariationMap,
      );
      setActiveStep(1);
    }
  };

  const handleVariationTemplateNext = async () => {
    if (
      !selectedTemplate ||
      createTemplateVariations.isLoading ||
      isSubmitting
    ) {
      return;
    }

    try {
      setIsSubmitting(true);
      shouldRemoveItemRef.current = false;

      const variations = Object.entries(variationMap)
        .map(([order, variation]) => ({
          order: Number(order),
          product_code: variation.variationCode,
          ...(variation.showFlavors !== undefined && {
            config: { showFlavors: variation.showFlavors },
          }),
        }))
        .sort((a, b) => a.order - b.order);

      const matchedVariation = product?.productVariations?.find(
        (v) => v.product_code === variationCode,
      );

      const payload = {
        template_id: selectedTemplate.id,
        variations,
        narrative: (
          narrativeGenerators[COLLECTIONS.VARIATION_TEMPLATES][
            USER_ACTIONS.CREATE
          ] as (name: string) => string
        )(matchedVariation?.name || variationCode),
      };

      await createTemplateVariations.mutateAsync(payload);
      shouldRemoveItemRef.current = false;
      Snackbar.success(lang.dyDisplaySavedNotPublished);
      navigate(`${PATHS.PRODUCT_EDIT(productCode)}#${variationCode}`);
    } catch (error) {
      Snackbar.error(`${lang.dyDisplay} ${lang.failedToSave}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTemplateClick = (template: IDyTemplate) => {
    setSelectedTemplate(template);
  };

  return {
    activeStep,
    discardModal,
    handleDiscard,
    handleDiscardConfirm,
    handleNext,
    handleTemplateClick,
    handleVariationTemplateNext,
    isLoading,
    isSaving: createTemplateVariations.isLoading || isSubmitting,
    selectedTemplate,
    setVariationMap,
    templates,
    variationMap,
  };
};
